import { useI18n } from "i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import useAuthActions from "modules/auth/hooks/useAuthActions";
import { useEventSso } from "modules/eventLanding/hooks/useEventSso";
import useAuth from "modules/auth/hooks/useAuth";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import { IEvent } from "modules/event/types";
import { usePrevious } from "helpers/reactHooksUtils";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import {
  hasAutoJoin,
  hasShouldProceedToEvent,
} from "modules/queryParams/queryParamsHelper";
import { useHasAnsweredQuestions } from "modules/registration";
import { useAppDispatch } from "store/hooks";
import {
  selectIsLoading,
  selectHasPayableTicket,
} from "modules/ticketing/redux/selectors";
import { useSelector } from "react-redux";
import { isUsersNameMissing } from "modules/auth/components/MissingFullNameDialog";
import { setShowTickets } from "modules/ticketing/redux/slice";
import { selectCurrentEvent, selectEventMember } from "modules/event/selectors";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { checkEmailInvited } from "modules/eventMemberList/eventMember.api";
import { selectUser } from "modules/auth/redux/selectors";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth } from "services/firebaseService/firebaseConfig";
import { setShowRegisterDialog } from "./redux/eventLandingSlice";

interface IEventAuthCheckerProps {
  setRedirectingToSSO: (redirecting: boolean) => void;
}

const EventAuthChecker = ({
  setRedirectingToSSO,
}: IEventAuthCheckerProps): null => {
  const dispatch = useAppDispatch();
  const intervalRef = useRef<number>();
  const { t } = useI18n("eventLanding");
  const [needCheck, setNeedCheck] = useState<boolean>(false);
  const event = useSelector(selectCurrentEvent);
  const eventMember = useSelector(selectEventMember);
  const { updateSAMLClaims } = useAuthActions();
  const { isAuthenticated } = useAuth();
  const wasAuthenticated = usePrevious(isAuthenticated);
  const { checkIfEventHappeningNow, checkIfEventEnded } = useCheckEventStatus();
  const user = useSelector(selectUser);
  const { goToEventMap, registerToEvent, isRegistered } = useRegisterToEvent();
  const { pathname } = window.location;
  const params = useQueryParams();

  const {
    hasAnsweredQuestions,
    hasQuestions,
    showRegistrationForm,
    status: questionStatus,
  } = useHasAnsweredQuestions(event?.id);

  const isAutoJoin = hasAutoJoin(params);
  const shouldProceedToEvent = hasShouldProceedToEvent(params);
  const isSSO = pathname.substring(pathname.lastIndexOf("/") + 1) === "sso";
  const isLoadingTicketInfo = useSelector(selectIsLoading);
  const hasPayableTicket = useSelector(selectHasPayableTicket);
  const clearIntervalRef = useCallback(() => {
    if (intervalRef.current) clearTimeout(intervalRef.current);
  }, []);

  const startEventRegistrationProcess =
    (isAuthenticated && isSSO) || shouldProceedToEvent;

  useEffect(() => {
    if (!event) return;
    if (
      isAuthenticated &&
      !wasAuthenticated &&
      questionStatus === "success" &&
      (!hasQuestions || hasAnsweredQuestions) &&
      checkIfEventHappeningNow(event)
    ) {
      goToEventMap(event, {
        ...(isAutoJoin && { autojoin: "1" }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    event,
    questionStatus,
    hasQuestions,
    hasAnsweredQuestions,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!event || !user || isLoadingTicketInfo || isUsersNameMissing(user)) {
      return;
    }

    const eventRegistrationProcess = (event: IEvent) => {
      if (!checkIfEventEnded(event)) {
        if (shouldProceedToEvent) {
          dispatch(setShowRegisterDialog(true));
        }
        registerToEvent(event.id);
      }
      if (checkIfEventHappeningNow(event)) {
        goToEventMap(event, {
          ...(isAutoJoin && { autojoin: "1" }),
        });
      }
    };

    const asyncFunction = async () => {
      if (startEventRegistrationProcess && questionStatus === "success") {
        if (event.isPrivate) {
          const { isSuccess, data } = await checkEmailInvited(
            event.id,
            user.email,
          );

          if (isSuccess && !data.invited && data.isPrivate) {
            dispatch(
              addErrorNotification({
                message: t("you.are.not.invited"),
              }),
            );
            return;
          }
        }

        const amIRegistered = event.id
          ? isRegistered(event.id, eventMember)
          : false;

        if (!amIRegistered && hasPayableTicket) {
          dispatch(setShowTickets(true));
        } else if (hasQuestions && !hasAnsweredQuestions) {
          showRegistrationForm();
        } else {
          eventRegistrationProcess(event);
        }
      }
    };
    asyncFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    event,
    user,
    isRegistered,
    isLoadingTicketInfo,
    startEventRegistrationProcess,
    hasQuestions,
    hasAnsweredQuestions,
    questionStatus,
  ]);

  useEventSso({
    setRedirectingToSSO,
    needCheck,
    clearIntervalRef,
  });

  useEffect(() => {
    if (isSSO) {
      intervalRef.current = window.setInterval(() => {
        setNeedCheck(true);
      }, 500);
    }

    onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        if (event) clearTimeout(intervalRef.current);

        user.providerData?.forEach((providerData) => {
          if (providerData?.providerId.includes("saml.")) {
            updateSAMLClaims(user);
          }
        });
      }
    });

    return clearIntervalRef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  return null;
};

export default EventAuthChecker;
